import React, { useState, useEffect } from 'react';
import io from 'socket.io-client';
import './Checkpoints.css';

const socket = io('http://localhost:3001');

function Checkpoints() {
  const [checkpoints, setCheckpoints] = useState([]);
  const [filterPlatform, setFilterPlatform] = useState('all');
  const [editingId, setEditingId] = useState(null);
  const [editDescription, setEditDescription] = useState('');

  useEffect(() => {
    loadCheckpoints();

    // Listen for new checkpoints
    socket.on('checkpoint:added', (checkpoint) => {
      setCheckpoints(prev => [checkpoint, ...prev]);
    });

    return () => {
      socket.off('checkpoint:added');
    };
  }, []);

  const loadCheckpoints = async () => {
    try {
      const response = await fetch('http://localhost:3001/api/checkpoints');
      const data = await response.json();
      setCheckpoints(data);
    } catch (error) {
      console.error('Error loading checkpoints:', error);
    }
  };

  const deleteCheckpoint = async (id) => {
    if (!window.confirm('Checkpoint wirklich löschen?')) return;

    try {
      await fetch(`http://localhost:3001/api/checkpoints/${id}`, {
        method: 'DELETE'
      });
      setCheckpoints(prev => prev.filter(cp => cp.id !== id));
    } catch (error) {
      console.error('Error deleting checkpoint:', error);
    }
  };

  const startEdit = (checkpoint) => {
    setEditingId(checkpoint.id);
    setEditDescription(checkpoint.description);
  };

  const saveEdit = async (id) => {
    try {
      await fetch(`http://localhost:3001/api/checkpoints/${id}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ description: editDescription })
      });
      
      setCheckpoints(prev => prev.map(cp => 
        cp.id === id ? { ...cp, description: editDescription } : cp
      ));
      setEditingId(null);
    } catch (error) {
      console.error('Error updating checkpoint:', error);
    }
  };

  const cancelEdit = () => {
    setEditingId(null);
    setEditDescription('');
  };

  const formatStreamTime = (seconds) => {
    if (!seconds || seconds === 0) return '--:--';
    
    const hours = Math.floor(seconds / 3600);
    const minutes = Math.floor((seconds % 3600) / 60);
    const secs = Math.floor(seconds % 60);
    
    if (hours > 0) {
      return `${hours}:${minutes.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`;
    }
    return `${minutes}:${secs.toString().padStart(2, '0')}`;
  };

  const formatDate = (timestamp) => {
    const date = new Date(timestamp);
    return date.toLocaleString('de-DE', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const getPlatformIcon = (platform) => {
    switch (platform) {
      case 'twitch-main': return '💜';
      case 'twitch-247': return '🔄';
      case 'youtube': return '▶️';
      case 'discord': return '💙';
      default: return '📌';
    }
  };

  const getPlatformName = (platform) => {
    switch (platform) {
      case 'twitch-main': return 'Twitch Main';
      case 'twitch-247': return 'Twitch 24/7';
      case 'youtube': return 'YouTube';
      case 'discord': return 'Discord';
      default: return platform;
    }
  };

  const filteredCheckpoints = filterPlatform === 'all' 
    ? checkpoints 
    : checkpoints.filter(cp => cp.platform === filterPlatform);

  return (
    <div className="checkpoints-page">
      <div className="checkpoints-header">
        <h1>📍 Stream Checkpoints</h1>
        <p>Wichtige Momente im Stream festhalten</p>
      </div>

      <div className="checkpoints-controls">
        <div className="filter-group">
          <label>Plattform:</label>
          <select value={filterPlatform} onChange={(e) => setFilterPlatform(e.target.value)}>
            <option value="all">Alle</option>
            <option value="twitch-main">Twitch Main</option>
            <option value="twitch-247">Twitch 24/7</option>
            <option value="youtube">YouTube</option>
            <option value="discord">Discord</option>
          </select>
        </div>
        
        <div className="stats">
          <span>Gesamt: {checkpoints.length}</span>
          <span>Gefiltert: {filteredCheckpoints.length}</span>
        </div>
      </div>

      <div className="checkpoints-list">
        {filteredCheckpoints.length === 0 ? (
          <div className="no-checkpoints">
            <p>Noch keine Checkpoints vorhanden.</p>
            <p>Nutze <code>!cp &lt;Beschreibung&gt;</code> im Chat, um einen Checkpoint zu erstellen.</p>
          </div>
        ) : (
          filteredCheckpoints.map(checkpoint => (
            <div key={checkpoint.id} className="checkpoint-card">
              <div className="checkpoint-header">
                <div className="checkpoint-platform">
                  <span className="platform-icon">{getPlatformIcon(checkpoint.platform)}</span>
                  <span className="platform-name">{getPlatformName(checkpoint.platform)}</span>
                </div>
                <div className="checkpoint-time">
                  <span className="stream-time">{formatStreamTime(checkpoint.stream_time)}</span>
                </div>
              </div>

              <div className="checkpoint-body">
                {editingId === checkpoint.id ? (
                  <div className="edit-mode">
                    <input
                      type="text"
                      value={editDescription}
                      onChange={(e) => setEditDescription(e.target.value)}
                      autoFocus
                    />
                    <div className="edit-actions">
                      <button className="save-btn" onClick={() => saveEdit(checkpoint.id)}>
                        ✓ Speichern
                      </button>
                      <button className="cancel-btn" onClick={cancelEdit}>
                        ✕ Abbrechen
                      </button>
                    </div>
                  </div>
                ) : (
                  <p className="checkpoint-description">{checkpoint.description}</p>
                )}
              </div>

              <div className="checkpoint-footer">
                <div className="checkpoint-meta">
                  <span className="created-by">👤 {checkpoint.created_by}</span>
                  <span className="timestamp">🕐 {formatDate(checkpoint.timestamp)}</span>
                </div>
                {editingId !== checkpoint.id && (
                  <div className="checkpoint-actions">
                    <button 
                      className="edit-btn"
                      onClick={() => startEdit(checkpoint)}
                      title="Bearbeiten"
                    >
                      ✏️
                    </button>
                    <button 
                      className="delete-btn"
                      onClick={() => deleteCheckpoint(checkpoint.id)}
                      title="Löschen"
                    >
                      🗑️
                    </button>
                  </div>
                )}
              </div>
            </div>
          ))
        )}
      </div>
    </div>
  );
}

export default Checkpoints;
